import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelectedPlanStore} from 'presentation/pages/others/selection-flow/store/useSelectedPlanStore';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {FeaturesEnum, ProfileEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {AccessManagementPermissionItem} from 'domain/usecases';
import {translations} from 'presentation/translations-new';
import {
	useGetAccessManagementPermissionsQuery,
	usePostAccessManagementPermissionsMutation,
} from '../../../../queries';
import {useShareAccessFeatureFlag} from 'presentation/pages/private/hooks/useShareAccessFeatureFlag';

const {successGranted, successRemoved} = translations['pt-br'].privateFlow.profile.dataSharing;

export function useDataSharing() {
	const navigate = useNavigate();
	const {isLaptop} = useMediaQueries();
	const {beneficiary, canChangeAccess} = useAccountInformation();
	const selectedPlan = useSelectedPlanStore(state => state.selectedPlan);
	const {isEnabled: isShareAccessEnabled} = useShareAccessFeatureFlag();
	const {isEnabled: isShareAccessFeaturesApiEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SHARE_ACCESS_FEATURES_API,
	);
	const [pendingItem, setPendingItem] = useState<AccessManagementPermissionItem | null>(null);
	const [showConsentDrawer, setShowConsentDrawer] = useState(false);
	const [showReadOnlyConsentDrawer, setShowReadOnlyConsentDrawer] = useState(false);
	const [showFeaturesDrawer, setShowFeaturesDrawer] = useState(false);
	const [showSubmitError, setShowSubmitError] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');

	const {
		data: permissionsData,
		isLoading,
		isError,
		refetch,
	} = useGetAccessManagementPermissionsQuery();
	const {mutate: postPermissions, isPending: isPostingPermission} =
		usePostAccessManagementPermissionsMutation();
	const dependents = permissionsData?.compartilheAcessoSeuPlano ?? [];

	const handlePermissionChange = (item: AccessManagementPermissionItem, nextValue: boolean) => {
		setPendingItem(item);
		if (nextValue) {
			setShowConsentDrawer(true);
		}
	};

	const postPendingPermission = (nextValue: boolean) => {
		if (!pendingItem) return;
		const permissionItem = pendingItem;
		setPendingItem(null);

		postPermissions(
			[
				{
					...(nextValue ? {aceiteTermo: nextValue} : {}),
					cdPessoaMembro: permissionItem.cdPessoaMembro,
					cdUsuarioMembro: permissionItem.cdUsuarioMembro,
					nuOrdemUsuario: permissionItem.nuOrdemUsuarioMembro,
					permitido: nextValue,
				},
			],
			{
				onSuccess: () => setSuccessMessage(nextValue ? successGranted : successRemoved),
				onError: () => setShowSubmitError(true),
			},
		);
	};

	const handleBack = () => {
		navigate(ProfileEnum.PROFILE);
	};

	const handleShowFeatures = () => {
		if (isShareAccessFeaturesApiEnabled) {
			navigate(ProfileEnum.DATA_SHARING_FEATURES);
			return;
		}
		setShowFeaturesDrawer(true);
	};

	return {
		isShareAccessEnabled,
		isShareAccessFeaturesApiEnabled,
		beneficiary,
		canChangeAccess,
		selectedPlan,
		dependents,
		isLoading,
		isError,
		refetch,
		isPostingPermission,
		showConsentDrawer,
		setShowConsentDrawer,
		handlePermissionChange,
		postPendingPermission,
		handleBack,
		handleShowFeatures,
		showReadOnlyConsentDrawer,
		setShowReadOnlyConsentDrawer,
		showFeaturesDrawer,
		setShowFeaturesDrawer,
		showSubmitError,
		setShowSubmitError,
		successMessage,
		setSuccessMessage,
		isLaptop,
		pendingItem,
		setPendingItem,
	};
}
